import { useRef } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { cx } from '../lib/cx';
import { Icon } from '../icons/Icon';
import { useFocusTrap, useLockBodyScroll, useOnEscape } from '../lib/hooks';

export interface DrawerProps {
  open?: boolean;
  onClose: () => void;
  title?: ReactNode;
  side?: 'left' | 'right';
  closeOnBackdrop?: boolean;
  children?: ReactNode;
  'aria-label'?: string;
  /** Accessible name for the ✕. English default; the app passes a translation. */
  closeLabel?: string;
}

/** Slide-over drawer — the mobile nav pattern (brief §7.3). Same focus/scroll
 *  semantics as Modal. */
export function Drawer({
  open = true,
  onClose,
  title,
  side = 'right',
  closeOnBackdrop = true,
  closeLabel = 'Close',
  children,
  ...rest
}: DrawerProps) {
  const ref = useRef<HTMLDivElement>(null);
  useOnEscape(onClose, open);
  useLockBodyScroll(open);
  useFocusTrap(ref, open);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={cx('tc-overlay', 'tc-overlay--drawer', side === 'left' && 'tc-overlay--left')}
      onMouseDown={closeOnBackdrop ? onClose : undefined}
    >
      <div
        ref={ref}
        className={cx('tc-drawer', `tc-drawer--${side}`)}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : rest['aria-label']}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="tc-drawer__head">
          {title ? <h2 className="tc-drawer__title">{title}</h2> : <span />}
          <button type="button" className="tc-modal__close" aria-label={closeLabel} onClick={onClose}>
            <Icon name="x" size={18} />
          </button>
        </header>
        <div className="tc-drawer__body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
